import { memo, useState, useRef, useEffect } from "react";
import { Button, Grid2, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Avatar } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { icon } from "@/app/style/icons";
import { alpha, useTheme } from "@mui/material/styles";
import ChatActionIcons from "./chatlist/ChatActionIcons";
import { RouteTo } from "@routing/RouteMap";
import { setSelectChat } from "@store/slices/chatSelection";
import { useQuery, useMutation } from "@apollo/client";
import { Query, Mutation, Chat } from "@/app/graphql/codegen";
import { FETCH_CHAT } from "@chat/graphql/queries/fetchChat";
import { TOGGLE_CHAT_PRIVACY } from "@chat/graphql/mutations/toggleChatPrivacy";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";
import LockOpenIcon from "@mui/icons-material/LockOpen";

interface SelectChatButtonProps {
    name: string;
    id: string;
    selected?: boolean;
    isUsedInDashboard?: boolean;
}

const SelectChatButton = memo(({ name, id, selected, isUsedInDashboard }: SelectChatButtonProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isHovered, setIsHovered] = useState(false);
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const nameRef = useRef<HTMLElement>(null);

    const [chat, setChat] = useState<Chat | null>(null);

    const { data } = useQuery<Pick<Query, "chat">>(FETCH_CHAT, {
        variables: { id },
    });

    useEffect(() => {
        if (data?.chat) {
            setChat(data.chat);
        }
    }, [data]);

    const [toggleChatPrivacyMutation] = useMutation(TOGGLE_CHAT_PRIVACY, {
        refetchQueries: [{ query: FETCH_CHATS }, { query: FETCH_CHAT, variables: { id } }],
    });

    const handleTogglePrivacy = (event: React.MouseEvent) => {
        event.stopPropagation();
        setPrivacyDialogOpen(true);
    };

    const handleConfirmPrivacyChange = async () => {
        try {
            await toggleChatPrivacyMutation({
                variables: { id },
            });
            setPrivacyDialogOpen(false);
        } catch (error) {
            console.error("Failed to toggle chat privacy:", error);
        }
    };

    const handleCancelPrivacyChange = () => {
        setPrivacyDialogOpen(false);
    };

    // Enable Enter key for privacy confirmation dialog
    useEnterKeyDialog({
        isOpen: privacyDialogOpen,
        onConfirm: handleConfirmPrivacyChange,
    });

    // Check if text is truncated and should show tooltip
    useEffect(() => {
        if (nameRef.current && name) {
            const element = nameRef.current;
            const isOverflowing = element.scrollWidth > element.clientWidth;
            setShowTooltip(isOverflowing);
        }
    }, [name]);

    // Opens chat details panel
    const openChatDetails = (event: React.MouseEvent) => {
        event.stopPropagation();

        dispatch(setSelectChat(id));
        isUsedInDashboard && navigate(`${RouteTo.CHAT}/${id}`);
    };

    // Navigates directly to chat view
    const openChat = (event: React.MouseEvent) => {
        event.stopPropagation();

        openChatDetails(event);
        dispatch(setSelectChat(id));

        navigate(`${RouteTo.CHAT}/${id}`);
    };

    return (
        <>
            <Button
                variant={"outlined"}
                fullWidth
                sx={{
                    justifyContent: "space-between", // Verteile den Inhalt, um Platz für Icons zu schaffen
                    textTransform: "none",
                    bgcolor: selected ? alpha(theme.palette.semantic.button.primary, 0.1) : "transparent",
                    borderColor: selected ? theme.palette.semantic.button.primary : undefined,
                    "&:hover": {
                        bgcolor: selected ? alpha(theme.palette.semantic.button.primary, 0.15) : undefined,
                        borderColor: selected ? theme.palette.semantic.button.primary : undefined,
                    },
                }}
                size={"small"}
                onClick={openChatDetails}
                onDoubleClick={openChat}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Grid2 container size={12} spacing={0.5} alignItems={"center"}>
                    <Grid2>
                        <Avatar sx={{ height: 22, width: 22, fontSize: 13 }}>{chat?.creatorName?.charAt(0)}</Avatar>
                    </Grid2>

                    <Grid2 size="grow" pl={1}>
                        <Tooltip title={name} disableHoverListener={!showTooltip} placement="bottom">
                            <Typography
                                ref={nameRef}
                                fontWeight={500}
                                align="left"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: showTooltip ? "help" : "default",
                                    maxWidth: "100%",
                                }}
                            >
                                {name}
                            </Typography>
                        </Tooltip>
                    </Grid2>

                    <Grid2 container>
                        {(isHovered || selected || chat?.isFavorite) && !isUsedInDashboard && (
                            <ChatActionIcons chatId={id} size="small" hideDeleteButton={chat?.isFavorite || !isHovered} />
                        )}

                        {data?.chat.isPrivate && (
                            <Grid2>
                                <IconButton size={"small"} onClick={handleTogglePrivacy}>
                                    <icon.lock sx={{ color: "semantic.icon.functional" }} />
                                </IconButton>
                            </Grid2>
                        )}

                        {!data?.chat.isPrivate && (isHovered || selected) && !isUsedInDashboard && (
                            <Grid2>
                                <IconButton size={"small"} onClick={handleTogglePrivacy}>
                                    <LockOpenIcon fontSize="small" sx={{ color: "semantic.icon.functional" }} />
                                </IconButton>
                            </Grid2>
                        )}

                        <Grid2>
                            <IconButton size="small" onClick={openChat} component="span">
                                <icon.openChat />
                            </IconButton>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Button>

            <Dialog open={privacyDialogOpen} onClose={handleCancelPrivacyChange}>
                <DialogTitle>{chat?.isPrivate ? "Chat öffentlich machen?" : "Chat privat machen?"}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {chat?.isPrivate
                            ? "Dieser Chat wird für alle Benutzer in Ihrer Organisation sichtbar sein. Sind Sie sicher, dass Sie ihn öffentlich machen möchten?"
                            : "Dieser Chat wird nur für Sie sichtbar sein. Sind Sie sicher, dass Sie ihn privat machen möchten?"}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPrivacyChange}>Abbrechen</Button>
                    <Button onClick={handleConfirmPrivacyChange} variant="contained" color="secondary">
                        Bestätigen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default SelectChatButton;
